import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, ArrowRight, GoogleLogo, Sun, Moon, Lock } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import Brand from "@/components/Brand";

const BG = "https://images.unsplash.com/photo-1765164478579-2c041dad2c7d?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85";

export default function Login() {
  const { login } = useAuth();
  const { mode, toggleMode } = useTheme();
  const [email, setEmail] = useState("demo@clariva.ai");
  const [password, setPassword] = useState("password123");
  const [loading, setLoading] = useState(false);
  const [ldapMode, setLdapMode] = useState(false);
  const navigate = useNavigate();

  // Detect whether this email requires LDAP auth (debounced on blur / email change)
  useEffect(() => {
    if (!email || !email.includes("@")) { setLdapMode(false); return; }
    const h = setTimeout(() => {
      api.get("/auth/sso-hint", { params: { email } })
        .then(r => setLdapMode(!!r.data?.ldap))
        .catch(() => setLdapMode(false));
    }, 500);
    return () => clearTimeout(h);
  }, [email]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally { setLoading(false); }
  };

  const googleSignIn = () => {
    const redirect = `${window.location.origin}/auth/google/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen relative">
      <button
        onClick={toggleMode}
        data-testid="login-theme-toggle"
        aria-label="Toggle dark mode"
        className="fixed top-5 right-5 z-20 w-10 h-10 rounded-full flex items-center justify-center bg-[var(--surface)]/80 backdrop-blur-md border border-[var(--border-strong)] text-[var(--text)] hover:bg-[var(--surface-2)] transition"
      >
        {mode === "dark" ? <Sun size={16} weight="bold" /> : <Moon size={16} weight="bold" />}
      </button>
      <div className="fixed inset-0 -z-10">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-30 dark:opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg)]/70 to-[var(--bg)]" />
      </div>
      <div className="min-h-screen flex items-center justify-center p-6">
        <motion.div
          initial={{ y: 40, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md rounded-2xl p-8 shadow-2xl backdrop-blur-2xl bg-[var(--bg-2)]/85 border border-[var(--border-strong)]"
        >
          <Link to="/" className="flex items-center gap-2 mb-8">
            <Brand size={32} />
            <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Clariva</div>
          </Link>

          <div className="overline mb-2">Sign in</div>
          <h1 className="text-3xl font-semibold tracking-tight mb-6 text-[var(--text)]" style={{ fontFamily: 'var(--font-heading)' }}>Welcome back</h1>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs text-[var(--text-2)]">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="mt-1.5 bg-[var(--surface)] border-[var(--border-strong)] text-[var(--text)]" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs text-[var(--text-2)]">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="mt-1.5 bg-[var(--surface)] border-[var(--border-strong)] text-[var(--text)]" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full h-11 gap-2">
              {loading ? "Signing in…" : (
                ldapMode ? <>Sign in with AD/LDAP <Lock size={14} /></> : <>Sign in <ArrowRight size={14} /></>
              )}
            </Button>
            {ldapMode && (
              <div className="text-[11px] text-[var(--text-3)] flex items-center gap-1.5" data-testid="ldap-mode-hint">
                <Lock size={11} weight="fill" /> Your organisation uses AD/LDAP — credentials will be validated against your directory.
              </div>
            )}
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-[var(--border)]" />
            <span className="text-xs text-[var(--text-3)]">OR</span>
            <div className="flex-1 h-px bg-[var(--border)]" />
          </div>

          <Button onClick={googleSignIn} variant="outline" data-testid="google-signin-btn" className="w-full h-11 gap-2 bg-[var(--surface)] border-[var(--border-strong)] hover:bg-[var(--surface-2)]">
            <GoogleLogo size={16} weight="bold" /> Continue with Google
          </Button>

          {/* Demo role chips */}
          <div className="mt-6 pt-5 border-t border-[var(--border)]">
            <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)] mb-2">Try the demo</div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { email: "admin@clariva.ai", label: "Global Admin", testid: "demo-admin" },
                { email: "support@clariva.ai", label: "Clariva Support", testid: "demo-support" },
                { email: "ceo@northwind.example", label: "Tenant Admin", testid: "demo-tenant-admin" },
                { email: "legal@northwind.example", label: "Contract Mgr", testid: "demo-contract-mgr" },
                { email: "finance@northwind.example", label: "Manager", testid: "demo-manager" },
                { email: "signer@northwind.example", label: "Signatory", testid: "demo-signatory" },
                { email: "viewer@northwind.example", label: "Viewer", testid: "demo-viewer" },
              ].map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => { setEmail(d.email); setPassword("password123"); }}
                  data-testid={d.testid}
                  className="text-[10px] px-2 h-6 rounded-full border border-[var(--border-strong)] bg-[var(--surface)] hover:bg-[var(--surface-2)] text-[var(--text-2)] transition"
                >
                  {d.label}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[var(--text-3)] mt-2">Password is <code>password123</code> for all demo accounts.</div>
          </div>

          <div className="mt-5 text-sm text-[var(--text-2)] text-center">
            New to Clariva?{" "}
            <Link to="/register" className="text-[var(--accent)] hover:underline" data-testid="register-link">Create account</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
